// Pure DSP helpers for MP3 encoding, shared by the one-shot encoder worker
// (mp3Encoder.worker.js, via Task 6) and the streaming encoder
// (mp3StreamSession.js + mp3StreamEncoder.worker.js). Kept free of `self` and
// lamejs so they run in jsdom and are unit-testable directly.

export const SAMPLES_PER_FRAME = 1152;
export const TARGET_RATE = 16000;

// Pick CBR bitrate from recording duration (matches the mobile app's strategy).
// Stereo (a telemed recording: clinician left, patient right, transcribed per
// channel) gets 16 kbps more, so each voice keeps most of the mono quality while
// the file grows by a third, not double. Every value is a valid MPEG-2 Layer III
// bitrate at 16 kHz.
export function pickBitrate(durationSecs, channels = 1) {
  const minutes = durationSecs / 60;
  let mono;
  if (minutes <= 30) mono = 48;
  else if (minutes <= 60) mono = 40;
  else if (minutes <= 120) mono = 32;
  else mono = 24;
  return channels === 2 ? mono + 16 : mono;
}

// Nearest-neighbour downsample. Good enough for speech; the encoder's lowpass
// filters high-freq artefacts.
export function downsample(samples, fromRate, toRate) {
  if (fromRate === toRate) return samples;
  const ratio = fromRate / toRate;
  const newLength = Math.floor(samples.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    result[i] = samples[Math.floor(i * ratio)];
  }
  return result;
}

// Float32 PCM (-1..1) -> Int16.
export function floatToInt16(samples) {
  const int16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    int16[i] = s < 0 ? s * 32768 : s * 32767;
  }
  return int16;
}

// Average N channels into one mono Float32Array of `length` samples.
export function mixToMono(channels, length) {
  const mono = new Float32Array(length);
  const n = channels.length;
  for (let c = 0; c < n; c++) {
    const ch = channels[c];
    for (let i = 0; i < length; i++) mono[i] += ch[i];
  }
  if (n > 1) {
    for (let i = 0; i < length; i++) mono[i] /= n;
  }
  return mono;
}

// One decoded chunk -> Int16 PCM at 16 kHz, one array per OUTPUT channel.
// outChannels 1 is the historical mono mix. outChannels 2 keeps the first two
// input channels separate (never mixed: each one is a different speaker); a mono
// input is duplicated onto both so the stream stays well-formed — the caller is
// told separately that this chunk was not truly two-channel.
export function toPcmChannels(channels, sampleRate, outChannels = 1) {
  const length = channels[0].length;
  if (outChannels !== 2) return [floatToInt16(downsample(mixToMono(channels, length), sampleRate, TARGET_RATE))];
  const left = floatToInt16(downsample(channels[0], sampleRate, TARGET_RATE));
  const right = channels.length > 1 ? floatToInt16(downsample(channels[1], sampleRate, TARGET_RATE)) : left;
  return [left, right];
}

// Uint8Array -> base64 (chunked to avoid a btoa stack overflow on big inputs).
export function uint8ArrayToBase64(bytes) {
  let binary = "";
  const CHUNK = 32768;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
